import { useState } from "react";
import { toast } from "react-hot-toast";
import { useHotkeys } from "react-hotkeys-hook";
import { HiCheck, HiEye, HiEyeOff, HiOutlineX } from "react-icons/hi";
import API from "../../api";
import { capture } from "../../instrument";
import { PASSWORD_MIN_LENGTH, hasPasswordLength, hasPasswordVariety, isPasswordStrong } from "../../utils";

const PASSWORD_ALPHABET = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!?%+-*";

const generatePassword = () => {
  const password = Array.from(crypto.getRandomValues(new Uint32Array(14)), (n) => PASSWORD_ALPHABET[n % PASSWORD_ALPHABET.length]).join("");
  return isPasswordStrong(password) ? password : generatePassword();
};

const AddUserPanel = ({ usernameSuffix, onClose, onCreated }) => {
  const [mode, setMode] = useState("email");
  const [form, setForm] = useState({ email: "", username: "", password: "", admin: false });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  useHotkeys("escape", onClose, { enableOnFormTags: true });

  const canSubmit = mode === "email" ? form.email.trim().length > 0 : form.username.trim().length > 0 && isPasswordStrong(form.password);

  const createUser = async () => {
    setSubmitting(true);
    try {
      const body = mode === "email" ? { email: form.email, admin: form.admin } : { username: form.username, password: form.password, admin: form.admin };
      const res = await API.post("/user/create", body);
      if (!res.ok) throw res;
      toast.success(mode === "email" ? "Invitation envoyée par e-mail" : "Compte créé");
      onCreated();
    } catch (error) {
      capture(error);
      toast.error(error.message);
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-[220] bg-gray-900/30" onMouseDown={onClose}>
      <form
        className="absolute inset-y-0 right-0 flex w-[440px] flex-col border-l border-gray-200 bg-white shadow-xl"
        onMouseDown={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit && !submitting) createUser();
        }}
      >
        <div className="flex items-center justify-between gap-4 border-b border-gray-100 px-6 py-5">
          <h2 className="text-base font-semibold text-gray-900">Nouvel utilisateur</h2>
          <button type="button" onClick={onClose} aria-label="Fermer" className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600">
            <HiOutlineX className="size-5" />
          </button>
        </div>

        <div className="flex grow flex-col gap-5 overflow-y-auto p-6">
          <div className="inline-flex gap-1 self-start rounded-lg bg-gray-100 p-1">
            <button
              type="button"
              onClick={() => setMode("email")}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${mode === "email" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
            >
              Par e-mail
            </button>
            <button
              type="button"
              onClick={() => setMode("username")}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${mode === "username" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
            >
              Par identifiant
            </button>
          </div>

          {mode === "email" ? (
            <>
              <div>
                <label htmlFor="add-user-email" className="mb-1 block text-sm font-medium text-gray-700">
                  Adresse e-mail
                </label>
                <input
                  id="add-user-email"
                  type="email"
                  autoFocus
                  autoComplete="off"
                  className="w-full rounded-lg border border-gray-200 p-3 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
                  placeholder="prenom.nom@laboratoire.fr"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <p className="text-sm text-gray-500">La personne reçoit un lien pour choisir son mot de passe. Aucun mot de passe ne transite par vous.</p>
            </>
          ) : (
            <>
              <div>
                <label htmlFor="add-user-username" className="mb-1 block text-sm font-medium text-gray-700">
                  Identifiant
                </label>
                <div className="flex items-center rounded-lg border border-gray-200 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500">
                  <input
                    id="add-user-username"
                    type="text"
                    autoFocus
                    autoComplete="off"
                    className="min-w-0 grow rounded-l-lg p-3 outline-none"
                    placeholder="accueil"
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                  />
                  <span className="pr-3 text-gray-400">@{usernameSuffix}</span>
                </div>
              </div>
              <div>
                <label htmlFor="add-user-password" className="mb-1 block text-sm font-medium text-gray-700">
                  Mot de passe initial
                </label>
                <div className="flex items-stretch gap-2">
                  <div className="flex grow items-center rounded-lg border border-gray-200 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500">
                    <input
                      id="add-user-password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      className="min-w-0 grow rounded-l-lg p-3 outline-none"
                      placeholder={`${PASSWORD_MIN_LENGTH} caractères minimum`}
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                      className="p-3 text-gray-400 transition-colors hover:text-gray-600"
                    >
                      {showPassword ? <HiEyeOff className="size-5" /> : <HiEye className="size-5" />}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setForm({ ...form, password: generatePassword() });
                      setShowPassword(true);
                    }}
                    className="inline-flex shrink-0 items-center rounded-lg border border-gray-200 px-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    Générer
                  </button>
                </div>
                <ul className="mt-2 space-y-1 text-xs">
                  <li className={`flex items-center gap-1.5 ${hasPasswordLength(form.password) ? "text-gray-700" : "text-gray-400"}`}>
                    <HiCheck className="size-3.5 shrink-0" />
                    {PASSWORD_MIN_LENGTH} caractères minimum
                  </li>
                  <li className={`flex items-center gap-1.5 ${hasPasswordVariety(form.password) ? "text-gray-700" : "text-gray-400"}`}>
                    <HiCheck className="size-3.5 shrink-0" />
                    Trois types de caractères parmi minuscules, majuscules, chiffres et spéciaux
                  </li>
                </ul>
              </div>
            </>
          )}

          <div className="flex items-center gap-3 border-t border-gray-100 pt-5">
            <button
              type="button"
              role="switch"
              aria-checked={form.admin}
              aria-labelledby="add-user-admin-label"
              onClick={() => setForm({ ...form, admin: !form.admin })}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                form.admin ? "bg-primary-500" : "bg-gray-200"
              }`}
            >
              <span
                className={`pointer-events-none inline-block size-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  form.admin ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
            <div>
              <p id="add-user-admin-label" className="text-sm font-medium text-gray-700">
                Administrateur
              </p>
              <p className="text-xs text-gray-500">Peut gérer les utilisateurs et la configuration du laboratoire.</p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-6 py-4">
          <button type="button" onClick={onClose} className="rounded-lg px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100">
            Annuler
          </button>
          <button
            type="submit"
            disabled={!canSubmit || submitting}
            className="rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {mode === "email" ? "Envoyer l'invitation" : "Créer le compte"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddUserPanel;
